import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  Flex,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { SearchBarInput } from 'packages/common-frontend/components/SearchAndCopyComponents';
import { Select } from 'packages/common-frontend/components/Select';
import { ErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import {
  Party,
  PartyType,
  PARTY_OPTIONS,
  Organization,
} from '~/core/routes/settings/implementations/common/types';
import { VirtualizedList } from '~/core/routes/settings/implementations/common/VirtualizedList';
import { useVisibleOrganizationsQuery } from '~/core/routes/settings/implementations/implementations.gql.generated';

type AddPartyModalProps = {
  onClose: () => void;
  isOpen: boolean;
  onAddParty: (party: Party) => void;
};
const AddPartyModalBase = ({
  onClose,
  isOpen,
  onAddParty,
}: AddPartyModalProps) => {
  const { user } = useUserAuthOrThrow();
  const [searchQuery, setSearchQuery] = useState('');
  const [partyType, setPartyType] = useState<PartyType>();

  const [
    selectedOrganization,
    setSelectedOrganization,
  ] = useState<Organization>();

  useEffect(() => {
    // Reset everything when the modal is opened
    if (isOpen) {
      setPartyType(undefined);
      setSelectedOrganization(undefined);
      setSearchQuery('');
    }
  }, [isOpen]);

  const { data, error } = useVisibleOrganizationsQuery({
    variables: { organizationId: user.organizationId },
  });
  if (error) throw error;

  const filteredData = useMemo(() => {
    const orgs = data?.organization.visibleOrganizations || [];
    return orgs.filter((org) => org.name.toLowerCase().includes(searchQuery));
  }, [searchQuery, data?.organization.visibleOrganizations]);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // This fixes an issue where focus on the search box can be lost while
    // deleting characters.
    const timeoutId = setTimeout(
      () => inputRef.current && inputRef.current.focus(),
      0,
    );
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Party</ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" flexDirection="column" gap="1rem">
          <FormControl>
            <Flex direction="column" gap="0.5rem">
              <FormLabel fontWeight="bold">Organization</FormLabel>
              <Menu matchWidth>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  variant="secondary-muted"
                  fontWeight={400}
                  width="100%"
                  textAlign="left"
                >
                  {selectedOrganization
                    ? selectedOrganization.name
                    : 'Select Organization'}
                </MenuButton>
                <MenuList p="1rem">
                  <SearchBarInput
                    query={searchQuery}
                    onChangeQuery={(value) =>
                      setSearchQuery(value.toLowerCase())
                    }
                    inputProps={{ mb: '0.6rem' }}
                    ref={inputRef}
                  />
                  <VirtualizedList
                    list={filteredData}
                    renderListItem={(item) => (
                      <MenuItem onClick={() => setSelectedOrganization(item)}>
                        {item.name}
                      </MenuItem>
                    )}
                  />
                </MenuList>
              </Menu>
            </Flex>

            <Flex direction="column" gap="0.5rem" mt="1rem">
              <FormLabel fontWeight="bold">Party Role</FormLabel>
              <Select
                value={partyType}
                placeholderLabel="Select Party Role"
                onChange={(value) => setPartyType(value)}
                options={PARTY_OPTIONS}
                width="100%"
              />
            </Flex>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="secondary"
            colorScheme="v2-aurora"
            mr={3}
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            colorScheme="v2-aurora"
            isDisabled={!selectedOrganization || !partyType}
            onClick={() => {
              if (selectedOrganization && partyType) {
                onAddParty({ ...selectedOrganization, type: partyType });
                onClose();
              }
            }}
          >
            Add Party
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const AddPartyModal = (props: AddPartyModalProps) => (
  <ErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <></>;
    }}
  >
    <AddPartyModalBase {...props} />
  </ErrorBoundary>
);
